/**
 * EraImagePlaceholder — when a chain step has no images (or its image URL is
 * broken), render an era-appropriate empty artifact instead of a chroma-green
 * placeholder. Each placeholder is a styled SVG/CSS stage tuned to its era's
 * material culture:
 *   - Stone: chiseled rectangle with faint pictogram strokes
 *   - Papyrus: blank scroll with horizontal line guides
 *   - Vellum: illuminated empty manuscript page with corner accents
 *   - Printed: woodcut-style frame with an inkblot
 *   - Newsprint: column rules, [PHOTO] placeholder
 *   - Typewriter: blank typed page with [PHOTOGRAPH] tag
 *   - Mimeograph: blue-ink memo blanks
 *   - CRT: phosphor screen with NO SIGNAL text
 *   - Web: clean diagonal-stripes placeholder with subject hint
 *
 * Methodology: even an absence carries the medium. The user understands at a
 * glance what kind of artifact would have been here. Negative space is the
 * lesson too.
 */
import { getEraMedium } from '../lib/eraMedia';
import { ImageOff } from 'lucide-react';

function PictogramStone() {
  return (
    <svg viewBox="0 0 200 100" preserveAspectRatio="xMidYMid meet" style={{ width: '60%', height: '60%', opacity: 0.35 }}>
      <g stroke="#3A2E1C" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* Crude figure */}
        <circle cx="48" cy="40" r="6" />
        <line x1="48" y1="46" x2="48" y2="68" />
        <line x1="42" y1="56" x2="54" y2="56" />
        <line x1="48" y1="68" x2="42" y2="80" />
        <line x1="48" y1="68" x2="54" y2="80" />
        {/* Sun */}
        <circle cx="160" cy="30" r="7" />
        <line x1="160" y1="14" x2="160" y2="20" />
        <line x1="160" y1="40" x2="160" y2="46" />
        <line x1="146" y1="30" x2="152" y2="30" />
        <line x1="168" y1="30" x2="174" y2="30" />
        {/* Wave/ground line */}
        <path d="M 20 88 Q 60 80, 100 88 T 180 88" />
      </g>
    </svg>
  );
}

function PapyrusBlankScroll() {
  return (
    <svg viewBox="0 0 220 110" preserveAspectRatio="xMidYMid meet" style={{ width: '78%', height: '78%', opacity: 0.4 }}>
      <g stroke="#3D2914" strokeWidth="0.6" fill="none">
        <line x1="14" y1="22" x2="206" y2="22" />
        <line x1="14" y1="40" x2="180" y2="40" />
        <line x1="14" y1="58" x2="206" y2="58" />
        <line x1="14" y1="76" x2="170" y2="76" />
        <line x1="14" y1="94" x2="190" y2="94" />
      </g>
    </svg>
  );
}

function VellumIlluminated() {
  return (
    <svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" style={{ width: '70%', height: '70%' }}>
      {/* Outer illuminated border */}
      <rect x="6" y="6" width="188" height="98" stroke="#C8A951" strokeWidth="1" fill="none" opacity="0.6" />
      <rect x="11" y="11" width="178" height="88" stroke="#B22222" strokeWidth="0.5" fill="none" opacity="0.55" />
      {/* Drop cap reservation */}
      <rect x="20" y="22" width="22" height="22" stroke="#B22222" strokeWidth="0.6" fill="none" opacity="0.5" />
      <text x="31" y="40" textAnchor="middle" style={{ fontFamily: "'Uncial Antiqua', serif", fontSize: 18, fill: '#B22222' }} opacity="0.7">∽</text>
      {/* Text-line marks */}
      <g stroke="#2D1810" strokeWidth="0.3" opacity="0.5">
        <line x1="48" y1="28" x2="180" y2="28" />
        <line x1="48" y1="36" x2="160" y2="36" />
        <line x1="22" y1="56" x2="180" y2="56" />
        <line x1="22" y1="64" x2="170" y2="64" />
        <line x1="22" y1="72" x2="180" y2="72" />
        <line x1="22" y1="80" x2="155" y2="80" />
        <line x1="22" y1="88" x2="180" y2="88" />
      </g>
    </svg>
  );
}

function PrintedWoodcut() {
  return (
    <svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" style={{ width: '70%', height: '70%', opacity: 0.45 }}>
      <rect x="20" y="14" width="160" height="84" stroke="#1A1A1A" strokeWidth="1.5" fill="none" />
      <rect x="26" y="20" width="148" height="72" stroke="#1A1A1A" strokeWidth="0.4" fill="none" />
      {/* Inkblot oval */}
      <ellipse cx="100" cy="56" rx="22" ry="10" fill="#1A1A1A" opacity="0.35" />
    </svg>
  );
}

function NewsprintPlaceholder() {
  return (
    <div style={{ display: 'flex', gap: 6, padding: '0 12px', width: '88%', height: '70%', alignItems: 'stretch', opacity: 0.45 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ flex: 1, borderLeft: '1px solid rgba(22,17,11,0.4)', paddingLeft: 6 }}>
          <div style={{ background: 'rgba(22,17,11,0.18)', height: 4, marginTop: 4 }} />
          <div style={{ background: 'rgba(22,17,11,0.10)', height: 3, marginTop: 4 }} />
          <div style={{ background: 'rgba(22,17,11,0.10)', height: 3, marginTop: 3 }} />
          {i === 1 && <div style={{ background: 'rgba(22,17,11,0.30)', height: 28, margin: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Old Standard TT', serif", fontSize: 8, color: 'rgba(22,17,11,0.7)' }}>[PHOTO]</div>}
          <div style={{ background: 'rgba(22,17,11,0.10)', height: 3, marginTop: 3 }} />
          <div style={{ background: 'rgba(22,17,11,0.10)', height: 3, marginTop: 3 }} />
        </div>
      ))}
    </div>
  );
}

function TypewriterPlaceholder() {
  return (
    <div style={{ width: '78%', padding: '14px 16px', opacity: 0.55, fontFamily: "'Special Elite', monospace", fontSize: 11, color: '#1F1A14', lineHeight: 1.6 }}>
      <div>┌────────────────────────┐</div>
      <div>│   [ PHOTOGRAPH HERE ]  │</div>
      <div>│                        │</div>
      <div>│         ✕              │</div>
      <div>│                        │</div>
      <div>└────────────────────────┘</div>
      <div style={{ marginTop: 4, fontSize: 9, opacity: 0.7 }}>caption — pending</div>
    </div>
  );
}

function CRTPlaceholder() {
  return (
    <div style={{ width: '60%', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", color: '#7AFF7A', textShadow: '0 0 6px rgba(122,255,122,0.45)' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.2em' }}>NO SIGNAL</div>
      <div style={{ fontSize: 8, opacity: 0.6, marginTop: 6 }}>// ARCHIVE OFFLINE</div>
      <div style={{ fontSize: 8, opacity: 0.6 }}>// CHANNEL: HISTORY</div>
      <div style={{ marginTop: 10, height: 1, background: 'rgba(122,255,122,0.4)', boxShadow: '0 0 6px rgba(122,255,122,0.6)' }} />
    </div>
  );
}

function WebPlaceholder({ subject }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <ImageOff size={28} color="rgba(255,255,255,0.5)" />
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.55)' }}>
        IMAGE PENDING
      </div>
      {subject && (
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 11, color: 'rgba(255,255,255,0.4)', maxWidth: 280, textAlign: 'center', fontStyle: 'italic' }}>
          {subject}
        </div>
      )}
    </div>
  );
}

export default function EraImagePlaceholder({ eraId, subject = '', label = '' }) {
  const m = getEraMedium(eraId);
  let inner;
  switch (eraId) {
    case 'era0':
    case 'era1':
      inner = <PictogramStone />; break;
    case 'era2':
      inner = <PapyrusBlankScroll />; break;
    case 'era3':
      inner = <VellumIlluminated />; break;
    case 'era4':
      inner = <PrintedWoodcut />; break;
    case 'era5':
      inner = <NewsprintPlaceholder />; break;
    case 'era6':
      inner = <TypewriterPlaceholder />; break;
    case 'era7':
      inner = <TypewriterPlaceholder />; break;  // mimeograph reuses typewriter look
    case 'era8':
      inner = <CRTPlaceholder />; break;
    case 'era9':
    default:
      inner = <WebPlaceholder subject={subject} />;
  }

  // Surface background sourced from eraMedia so the placeholder reads as the
  // real medium would. era9 stays transparent (matches our content surface).
  const bg = eraId === 'era9' ? 'transparent' : m.background;

  return (
    <div
      data-testid={`era-image-placeholder-${eraId}`}
      style={{
        width: '100%',
        height: '100%',
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {inner}
      {label && (
        <div
          style={{
            position: 'absolute',
            bottom: 6,
            right: 8,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 8,
            letterSpacing: '0.18em',
            color: m.ink,
            opacity: 0.45,
            pointerEvents: 'none',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}
